// src/app/pages/cost-metrics/net-benefits/net-benefits.ts
import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
// PrimeNG ToggleSwitch
import { ToggleSwitchModule } from 'primeng/toggleswitch';

type RangeKey = 'three' | 'five' | 'ten' | 'fifteen';
type Cm = Record<string, Record<string, number>> | null;

@Component({
    selector: 'app-net-benefit',
    standalone: true,
    imports: [FormsModule, ToggleSwitchModule],
    templateUrl: './net-benefits.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NetBenefitsComponent implements OnInit, OnChanges {
    @Input() baseCm: Cm = null; // Basic Simulation (initial-response.json)
    @Input() currentCm: Cm = null; // Selected simulation from parent dropdown
    @Input() range: RangeKey = 'three';
    @Input() yearKey?: string; // optional; period mapping will set target year
    @Input() hasComparison = false; // if false, show hint and hide tables

    // Toggle: OFF (Health Sector Cost) by default
    isBroaderSocietal = false;

    // UI flags
    showHint = false;

    // Health Sector Cost section
    healthSector = {
        intervention: { base: '0', current: '0' },
        healthSystem: { base: '0', current: '0' },
        total: { base: '0', current: '0' }
    };

    // Quality Adjusted Life Years section
    qaly = {
        qalys: { base: '0', current: '0' },
        valuePerQaly: '0', // current simulation only (scalar)
        monetised: { base: '0', current: '0' }
    };

    // Broader Societal Cost section
    societal = {
        societal: { base: '0', current: '0' },
        healthSector: { base: '0', current: '0' },
        total: { base: '0', current: '0' }
    };

    // Scenario Comparison section
    scenario = {
        monetisedQaly: { base: '0', current: '0' },
        selectedCostLabel: 'Health Sector Cost',
        selectedCost: { base: '0', current: '0' }, // always shown with '-' in template
        total: { base: '0', current: '0' },
        netBenefit: '0'
    };

    ngOnInit(): void {
        this.computeAll();
    }

    ngOnChanges(_: SimpleChanges): void {
        this.computeAll();
    }

    onToggle(): void {
        // Update label and recompute on toggle
        this.computeAll();
    }

    private computeAll(): void {
        // Hide content and show hint when no current simulation is available
        this.showHint = !this.currentCm && !this.hasComparison;

        const targetYear = this.getTargetYearForRange(this.range);

        // Health Sector Cost (section-level display)
        this.healthSector.intervention.base = this.readScaled(this.baseCm, `cummulative_intervention_cost_current_${this.range}`, targetYear);
        this.healthSector.intervention.current = this.readScaled(this.currentCm, `cummulative_intervention_cost_current_${this.range}`, targetYear);

        this.healthSector.healthSystem.base = this.readScaled(this.baseCm, `cummulative_health_sector_cost_current_${this.range}`, targetYear);
        this.healthSector.healthSystem.current = this.readScaled(this.currentCm, `cummulative_health_sector_cost_current_${this.range}`, targetYear);

        // Display-only "Total" mapped field
        this.healthSector.total.base = this.readScaled(this.baseCm, `cummulative_invention_health_sector_cost_current_${this.range}`, targetYear);
        this.healthSector.total.current = this.readScaled(this.currentCm, `cummulative_invention_health_sector_cost_current_${this.range}`, targetYear);

        // QALYs
        this.qaly.qalys.base = this.readScaled(this.baseCm, `cummulative_QALY_current_${this.range}`, targetYear);
        this.qaly.qalys.current = this.readScaled(this.currentCm, `cummulative_QALY_current_${this.range}`, targetYear);

        // valued_each_QALY (current only; scalar or series)
        this.qaly.valuePerQaly = this.readRawFormatted(this.currentCm, `valued_each_QALY`, targetYear);

        // Monetised QALY gains (considered "total" for QALYs)
        const monetisedQalyBaseRaw = this.readValue(this.baseCm, `cummulative_monetized_QALY_gains_current_${this.range}`, targetYear);
        const monetisedQalyCurrentRaw = this.readValue(this.currentCm, `cummulative_monetized_QALY_gains_current_${this.range}`, targetYear);
        this.qaly.monetised.base = this.formatScaled(monetisedQalyBaseRaw);
        this.qaly.monetised.current = this.formatScaled(monetisedQalyCurrentRaw);

        // Broader Societal Cost (section-level display)
        this.societal.societal.base = this.readScaled(this.baseCm, `cumulative_social_cost_current_${this.range}`, targetYear);
        this.societal.societal.current = this.readScaled(this.currentCm, `cumulative_social_cost_current_${this.range}`, targetYear);

        this.societal.healthSector.base = this.readScaled(this.baseCm, `cummulative_invention_health_sector_cost_current_${this.range}`, targetYear);
        this.societal.healthSector.current = this.readScaled(this.currentCm, `cummulative_invention_health_sector_cost_current_${this.range}`, targetYear);

        this.societal.total.base = this.readScaled(this.baseCm, `cummulative_invention_health_sector_social_cost_current_${this.range}`, targetYear);
        this.societal.total.current = this.readScaled(this.currentCm, `cummulative_invention_health_sector_social_cost_current_${this.range}`, targetYear);

        // Scenario Comparison (computed totals and net benefit with toggle)
        const selectedCostKey = this.isBroaderSocietal ? `cummulative_invention_health_sector_social_cost_current_${this.range}` : `cummulative_invention_health_sector_cost_current_${this.range}`;

        this.scenario.selectedCostLabel = this.isBroaderSocietal ? 'Broader Societal Cost' : 'Health Sector Cost';

        const selectedCostBaseRaw = this.readValue(this.baseCm, selectedCostKey, targetYear);
        const selectedCostCurrentRaw = this.readValue(this.currentCm, selectedCostKey, targetYear);

        const baseTotalRaw = monetisedQalyBaseRaw - selectedCostBaseRaw;
        const currentTotalRaw = monetisedQalyCurrentRaw - selectedCostCurrentRaw;

        // Net Benefit = currentTotal − baseTotal
        const netBenefitRaw = currentTotalRaw - baseTotalRaw;

        // Assign formatted values
        this.scenario.monetisedQaly.base = this.formatScaled(monetisedQalyBaseRaw);
        this.scenario.monetisedQaly.current = this.formatScaled(monetisedQalyCurrentRaw);

        // Always show selected cost with a leading minus in template; format as positive string here
        this.scenario.selectedCost.base = this.formatScaled(Math.abs(selectedCostBaseRaw));
        this.scenario.selectedCost.current = this.formatScaled(Math.abs(selectedCostCurrentRaw));

        this.scenario.total.base = this.formatScaled(baseTotalRaw);
        this.scenario.total.current = this.formatScaled(currentTotalRaw);

        this.scenario.netBenefit = this.formatNetBenefit(netBenefitRaw);
    }

    private formatNetBenefit(n: number): string {
        const abs = Math.abs(n);
        if (abs >= 1_000_000_000) {
            const scaled = n / 1_000_000_000;
            return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(scaled)}B`;
        }
        // < 1B: show in Millions, no decimals, round up
        const roundedUpMillions = Math.ceil(abs / 1_000_000);
        const signed = n < 0 ? -roundedUpMillions : roundedUpMillions;
        return `${signed}M`;
    }

    // Map period suffix to target year
    private getTargetYearForRange(r: RangeKey): string {
        switch (r) {
            case 'three':
                return '2028';
            case 'five':
                return '2030';
            case 'ten':
                return '2035';
            case 'fifteen':
                return '2040';
        }
    }

    // Read and scale to M/B with up to 2 decimals
    private readScaled(cm: Cm, key: string, targetYear: string): string {
        const raw = this.readValue(cm, key, targetYear);
        return this.formatScaled(raw);
    }

    // Read raw number and format with thousand separators (for valued_each_QALY)
    private readRawFormatted(cm: Cm, key: string, targetYear: string): string {
        const raw = this.readValue(cm, key, targetYear);
        return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(raw);
    }

    // Generic value reader with tolerant key resolution and nearest-past year fallback
    private readValue(cm: Cm, exactKey: string, targetYear: string): number {
        if (!cm) return 0;

        // Try exact key first
        let series = cm[exactKey];

        // Fallback: tolerant key resolution (case/snake/camel), stripping "current" for matching
        if (!series) {
            const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
            const want = norm(exactKey).replace(/current/g, '');
            const entry = Object.keys(cm)
                .map((k) => ({ k, n: norm(k).replace(/current/g, '') }))
                .find((e) => e.n.includes(want));
            if (entry) series = cm[entry.k];
        }

        if (!series || typeof series !== 'object') return 0;

        // Direct year
        if (Object.prototype.hasOwnProperty.call(series, targetYear)) {
            const v = series[targetYear];
            return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
        }

        // Nearest past fallback
        const years = Object.keys(series)
            .map((y) => Number(y))
            .filter((y) => Number.isFinite(y))
            .sort((a, b) => a - b);

        if (!years.length) return 0;

        const t = Number(targetYear);
        const past = years.filter((y) => y <= t).pop() ?? years[0];
        const v = series[String(past)];
        return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
    }

    // Scaled formatting: ≥1B -> B, else M; up to 2 decimals
    private formatScaled(n: number): string {
        const abs = Math.abs(n);
        if (abs >= 1_000_000_000) {
            const scaled = n / 1_000_000_000;
            return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(scaled)}B`;
        }
        const scaled = n / 1_000_000;
        return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(scaled)}M`;
    }
}
